import type { SVGProps } from "react";
import { SITE } from "../config";

type MarkMode = "brand" | "white";

type MarkProps = SVGProps<SVGSVGElement> & {
  /**
   * `brand` (default) — indigo→violet gradient stroke. Use on light and dark surfaces.
   * `white` — solid white stroke. Only for brand-gradient surfaces without a containing card.
   */
  mode?: MarkMode;
};

/**
 * ZopDesk logo mark: outline envelope with a V-fold and an amber notification dot.
 *
 * Geometry (48×48 viewBox):
 *   - Envelope: rounded rect (4,12)→(44,38), rx=4, stroke 2.4
 *   - V-fold: `M 6 14 L 24 28 L 42 14` — endpoints on the rounded-corner 45° arcs
 *   - Pip: circle (43,13) r=3.8, fill amber-500 (#f59e0b)
 */
export function LogoMark({ mode = "brand", ...props }: MarkProps) {
  const stroke = mode === "white" ? "#ffffff" : "url(#zd-mark-g)";
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${SITE.productName} logo`}
      {...props}
    >
      {mode === "brand" && (
        <defs>
          <linearGradient id="zd-mark-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
      )}
      <rect
        x="4"
        y="12"
        width="40"
        height="26"
        rx="4"
        stroke={stroke}
        strokeWidth="2.4"
        fill="none"
      />
      <path
        d="M 6 14 L 24 28 L 42 14"
        stroke={stroke}
        strokeWidth="2.4"
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="43" cy="13" r="3.8" fill="#f59e0b" />
    </svg>
  );
}

type LogoProps = {
  /**
   * Surface the logo is rendered on:
   *   - `light` (default): mark uses brand gradient stroke; Desk in ink-700
   *   - `dark`: mark uses brand gradient stroke (glows on ink-900); Desk in ink-200
   *   - `onBrand`: mark uses WHITE stroke — for when parent is a brand gradient and not contained in a card
   */
  variant?: "light" | "dark" | "onBrand";
  className?: string;
  size?: "sm" | "md";
};

/**
 * Canonical ZopDesk lockup: mark + wordmark.
 * Wordmark renders "Zop" in an indigo→violet gradient and "Desk" in a muted
 * neutral so the brand root is the hero and Desk is the supporting word.
 */
export function Logo({
  variant = "light",
  className,
  size = "md",
}: LogoProps) {
  const markSize = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  const textSize = size === "sm" ? "text-sm" : "text-[15px]";
  const markMode: MarkMode = variant === "onBrand" ? "white" : "brand";

  // On dark and brand-gradient surfaces, use a plain wordmark in a single
  // color. The gradient stroke of the mark already carries the brand color —
  // repeating the gradient in ~15px wordmark text reads muddy.
  if (variant !== "light") {
    return (
      <span
        className={`inline-flex items-center gap-2.5 ${className ?? ""}`}
        aria-label={SITE.productName}
      >
        <LogoMark mode={markMode} className={`${markSize} flex-none`} />
        <span
          className={`${textSize} font-semibold tracking-tight text-white`}
        >
          {SITE.productName}
        </span>
      </span>
    );
  }

  // Light surface: gradient Zop + muted Desk (the intentional brand lockup)
  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className ?? ""}`}
      aria-label={SITE.productName}
    >
      <LogoMark mode={markMode} className={`${markSize} flex-none`} />
      <span className={`${textSize} font-semibold tracking-tight`}>
        <span className="bg-gradient-to-br from-brand-500 to-violet-500 bg-clip-text text-transparent">
          Zop
        </span>
        <span className="text-ink-700">Desk</span>
      </span>
    </span>
  );
}
